<script setup lang="ts">
import { mdiPlay, mdiHeartOutline, mdiPuzzleOutline, mdiGamepadVariant } from '@mdi/js'
import type { Work } from '~/composables/useApi'

interface Props {
  modelValue?: boolean
  work?: Work | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  work: null,
})

const emit = defineEmits<{ 'update:modelValue': [boolean] }>()

const visible = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
})

// #750: クレジット（作り手）は隠し機能。既定は非表示で、隠しドットを見つけた人だけ開ける
// （#743 の hidden-in-plain-sight と同じ思想。データは公開エンドポイントから取得）。
const creditsRevealed = ref(false)
watch(visible, (open) => {
  if (!open) creditsRevealed.value = false
})

// #940: 公開作品への通報（権利の侵害など）。目立たせず、作品情報のいちばん下に置く
const reportOpen = ref(false)
</script>

<template>
  <DialogMobileDialog
    v-model="visible"
    :title="work?.title"
  >
    <div
      v-if="work?.author || work?.publishedAt"
      class="meta"
    >
      <span
        v-if="work?.publishedAt"
        class="meta__date"
      >{{ work.publishedAt }}</span>
      <span
        v-if="work?.author"
        class="meta__author-row"
      >
        <span class="meta__by">by</span>
        <span class="meta__author">{{ work.author }}</span>
      </span>
    </div>
    <p
      v-if="work?.description"
      class="description"
    >
      {{ work.description }}
    </p>
    <div class="stats">
      <span
        class="stats__item"
        aria-label="プレイ数"
      >
        <v-icon
          :icon="mdiPlay"
          size="small"
          aria-hidden="true"
        />
        <span>{{ work?.plays ?? 0 }}</span>
      </span>
      <span
        class="stats__item"
        aria-label="いいね数"
      >
        <v-icon
          :icon="mdiHeartOutline"
          size="small"
          aria-hidden="true"
        />
        <span>{{ work?.likes ?? 0 }}</span>
      </span>
      <v-tooltip
        v-if="work?.remixable"
        text="デコード可"
        location="top"
      >
        <template #activator="{ props: tooltipProps }">
          <span
            v-bind="tooltipProps"
            class="stats__item"
            aria-label="デコード可"
          >
            <v-icon
              :icon="mdiPuzzleOutline"
              size="small"
              aria-hidden="true"
            />
          </span>
        </template>
      </v-tooltip>
      <v-tooltip
        v-if="work?.gamepadCompatible"
        text="仮想ゲームパッド対応"
        location="top"
      >
        <template #activator="{ props: tooltipProps }">
          <span
            v-bind="tooltipProps"
            class="stats__item"
            aria-label="仮想ゲームパッド対応"
          >
            <v-icon
              :icon="mdiGamepadVariant"
              size="small"
              aria-hidden="true"
            />
          </span>
        </template>
      </v-tooltip>
    </div>
    <section
      v-if="work?.description2"
      class="editorial"
    >
      <p class="editorial__body">
        {{ work.description2 }}<span class="editorial__signature">（Dr.D）</span>
      </p>
    </section>
    <!-- #835: 著作表記が必須の素材を含む作品では、隠しドットを押していなくても
         WorkCredits 自身が表示を強制する（判定にレスポンスが要るので常に mount する）。 -->
    <WorkCredits
      v-if="work?.slug"
      :slug="work.slug"
      :revealed="creditsRevealed"
    />
    <footer class="credits">
      <a
        class="credits__link"
        href="https://github.com/kitao/pyxel"
        target="_blank"
        rel="noopener"
      >Made with Pyxel</a>
      <span class="credits__sep">·</span>
      <span>Powered by Code &amp; Magic</span>
      <!-- #750: 隠しクレジット入口。ラベル/ツールチップなしの極小ドット（発見しにくさのみ）。 -->
      <button
        type="button"
        class="hidden-credits-dot"
        aria-hidden="true"
        tabindex="-1"
        @click="creditsRevealed = true"
      />
    </footer>
    <div
      v-if="work?.slug"
      class="report-link"
    >
      <button
        type="button"
        class="report-link__button"
        data-testid="work-report-open"
        @click="reportOpen = true"
      >
        この作品を報告する
      </button>
    </div>
    <DialogWorkReportDialog
      v-if="work?.slug"
      v-model="reportOpen"
      :slug="work.slug"
      :title="work.title"
    />
  </DialogMobileDialog>
</template>

<style scoped>
.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  align-items: baseline;
  font-size: 0.85rem;
  margin-bottom: 8px;
}

.meta__date {
  color: rgba(60, 40, 20, 0.7);
}

.meta__author-row {
  display: inline-flex;
  gap: 4px;
  align-items: baseline;
}

.meta__by {
  color: rgba(60, 40, 20, 0.7);
}

.meta__author {
  color: #1f3554;
  font-weight: 600;
}

.description {
  white-space: pre-wrap;
  margin: 0 0 12px;
  color: #2a1f10;
}

.stats {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 0.9rem;
  color: rgba(60, 40, 20, 0.85);
  margin-bottom: 12px;
}

.stats__item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  /* アイコン単独と icon+数字 が混在しても視覚的に揃うよう、
     アイテム自体の最小幅を確保 */
  min-width: 1.5em;
  justify-content: flex-start;
}

.editorial {
  margin-top: 16px;
  padding: 12px 14px;
  /* 全体と同系で控えめな寸評ブロック — 上下に金色の細線で区切り */
  background: rgba(255, 255, 255, 0.35);
  border-top: 1px solid #c9a440;
  border-bottom: 1px solid #c9a440;
}

.editorial__body {
  /* 寸評本文は明朝体で */
  font-family: "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", "Noto Serif", serif;
  margin: 0;
  font-size: 0.9rem;
  white-space: pre-wrap;
  line-height: 1.7;
  color: #2a1f10;
}

.editorial__signature {
  /* (Dr.D) 署名 — 表示時に末尾に付加 */
  margin-left: 0.4em;
  color: rgba(31, 53, 84, 0.7);
  font-size: 0.85em;
}

.credits {
  position: relative;
  margin-top: 16px;
  font-size: 0.75rem;
  color: rgba(60, 40, 20, 0.55);
  text-align: center;
  letter-spacing: 0.02em;
}

.credits__link {
  color: inherit;
  text-decoration: none;
  border-bottom: 1px dotted rgba(60, 40, 20, 0.4);
}

.credits__link:hover {
  color: #1f3554;
  border-bottom-color: #1f3554;
}

.report-link {
  margin-top: 6px;
  text-align: center;
}

.report-link__button {
  font-size: 0.7rem;
  color: rgba(60, 40, 20, 0.5);
  text-decoration: underline dotted;
  background: none;
  border: none;
  cursor: pointer;
}

.report-link__button:hover {
  color: #1f3554;
}

.credits__sep {
  margin: 0 0.6em;
  opacity: 0.6;
}

.hidden-credits-dot {
  position: absolute;
  right: 6px;
  bottom: 6px;
  width: 8px;
  height: 8px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 50%;
  background: #000;
  opacity: 0.15;
  cursor: pointer;
}
</style>
